import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import GooglePlaces from './GooglePlaces';
import { EpaQuestions, PowerPoleQuestions, FenceAreaOption, FencingStock, NewJobQuoteItemExtra, ItemOptionList } from './models/JobInterfaces';
import { GenericList } from './models/JobSharedInterfaces';
import NewJobPricelistPowerPoleQuestionsData from './NewJobPricelistPowerPoleQuestions';
import JobQuoteEpaQuestionsData from './JobQuoteEpaQuestions';

const win: any = window;
const Stimulsoft = win.Stimulsoft;

interface JobScheduleAddNewProps {
    jobId: number
    isEvent: boolean;
    customerId: number;
    siteAddressDuration: number;
    needsPickup: boolean;
    showModal: boolean;
    hideModal(): void;
    save(): void;
}

interface NewItem {
    itemId: number;
    item: string;
    itemTypeId: number;
    productGroupId: number;
    chargeGst: boolean;
    scheduleDate: string;
    hirePeriod: number;
    defaultHirePeriod: number;
    customHirePeriod: boolean;
    jobInstalled: boolean;
    jobInvoiced: boolean;
    jobInvoicedDate: string;
    sendEmailToCustomer: boolean;
    usePerMetrePricing: boolean;
    chargeCalloutFee: boolean;   
    frequencyId: number;
    locationId: number;
    fenceBraceId: number;
    fenceBraceQuantity: number;     //used when selecting custom bracing when adding fencing
    fenceBracePrice: number;
    fenceBraceOverhireRate: number;
    fenceAreas: FenceAreaOption[];
    size: number;
    extraHours: number;
    quantity: number;
    extras: NewJobQuoteItemExtra;
    fencingStock: FencingStock;
    initialHire: number;
    travel: number;
    cleaningFee: number;
    trenchFee: number;
    fuelSurcharge: number;
    confinedSpaceAmount: number;
    sampleTestingAmount: number;
    wasteDisposalAmount: number;
    amount: number;
    overhireRate: number;
    truckExtraHours: number;
    unitNumber: string;
    truckDoorsId: number;
    cameraLocation: string;
    offHireDate: string;
    pickupDate: string;
    notes: string;
    isEws: boolean;
    isEvent: boolean;
    installOnly: boolean;
    extendable: boolean;
    itemTypeGroupId: number;
    trolley: boolean;
    purchaseOrderNumber: string;
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
    invoiceDescription: string;
    invoicePeriodTypeId: number;    //1 = Weeks, 2 = Months
    createManualPickup: boolean;    //used with fence re-instate to create a manual job for a pickup
}


const JobScheduleAddNewDataPopup = (props: JobScheduleAddNewProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [loading, setLoading] = React.useState(true);
    const initNewItem: NewItem = {
        itemId: -1,
        item: "",
        itemTypeId: -1,
        itemTypeGroupId: -1,
        productGroupId: 0,
        chargeGst: true,
        hirePeriod: 0,
        defaultHirePeriod: 42,
        customHirePeriod: false,
        sendEmailToCustomer: false,
        jobInstalled: false,
        jobInvoiced: false,
        jobInvoicedDate: "",
        usePerMetrePricing: false,
        chargeCalloutFee: false,
        frequencyId: -1,
        locationId: -1,
        fenceBraceId: -1,
        fenceBraceQuantity: 0,
        fenceBracePrice: 0,
        fenceBraceOverhireRate: 0,
        fenceAreas: [],
        scheduleDate: "",
        size: 0,
        extraHours: 0,
        quantity: 1,
        extras: {
            itemId: -1,
            quantity: 0,
            equipped: false
        },
        fencingStock: {
            panels: 0,
            blocks: 0,
            clamps: 0,
            pickets: 0,
            signs: 0,
            braces: 0
        },
        initialHire: 0,
        travel: 0,
        cleaningFee: 0,
        trenchFee: 0,
        fuelSurcharge: 0,
        confinedSpaceAmount: 0,
        sampleTestingAmount: 0,
        wasteDisposalAmount: 0,
        amount: 0,
        overhireRate: 0,
        truckExtraHours: 0,
        truckDoorsId: -1,
        cameraLocation: "",
        unitNumber: "",
        offHireDate: "",
        pickupDate: "",
        notes: "",
        isEws: false,
        isEvent: false,
        installOnly: false,
        extendable: true,
        trolley: false,
        purchaseOrderNumber: "",
        deliveryAddress: "",
        deliveryAddressGooglePlaceId: "",
        deliveryAddressLatitude: 0,
        deliveryAddressLongitude: 0,
        invoiceDescription: "",
        invoicePeriodTypeId: 1,
        createManualPickup: false
    };
    const [newItem, setNewItem] = React.useState<NewItem>(initNewItem);
    const [gateCode, setGateCode] = React.useState("");
    const [powerPoleQuestions, setPowerPoleQuestions] = React.useState<PowerPoleQuestions>({
        isUnderground: false,
        superSafeConnecting: -1,
        superSafeSupplierId: -1,
        powerPoleLocationId: -1,
        powerPreviouslyConnected: -1,
        powerAbolished: -1,
        siteLocked: -1,
        clearSightLine: -1,
        pitOnSite: -1,
        pitDistanceId: -1,
        superSafeDiggingTrench: -1
    });
    const [epaItemQuestions, setEpaItemQuestions] = React.useState<EpaQuestions>({
        tankLidExposed: -1,
        tankLidLocationKnown: -1,
        tankLidLocation: "",
        truckLargeAccess: -1,
        truckSmallAccess: -1,
        truckAccessToTank: -1,  //within 25m
        steepDriveway: -1,
        hoseMetres: 0,
        sealedDriveway: -1,
        lastService: "",
        waterBoard: "",
        waterAccessOnSite: -1,
        emergencySameDayJob: -1,
        weekendJob: -1,
        siteVisit: -1,
        confinedSpace: -1,
        materialSafetyDataSheet: -1,
        sampleTestingRequired: -1
    });
    const [itemQuestions, setItemQuestions] = React.useState({ showQuestions: false, itemType: 0}); //itemtype: 1 Powerpoles, 2 EPA
    const [schedulePickup, setSchedulePickup] = React.useState(props.isEvent);
    const [extras, setExtas] = React.useState({ tables: 0, chairs: 0 });
    const [extraId, setExtraId] = React.useState({ siteOfficeExtraId: 0, lunchRoomExtraId: 0 });
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [calloutFee, setCalloutFee] = React.useState(0);
    const [customerInvQuoted, setCustomerInvQuoted] = React.useState(false);
    const [canSendEmailToCustomer, setCanSendEmailToCustomer] = React.useState(true);
    const [epaLidLocationFeeFormatted, setEpaLidLocationFeeFormatted] = React.useState("$0.00");
    const [confinedSpaceFee, setConfinedSpaceFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [truckHireFee, setTruckHireFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [weekendFee, setWeekendFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [emergencyFee, setEmergencyFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [chargeFuelSurcharge, setChargeFuelSurcharge] = React.useState(false);
    const [fuelSurchargePumpClean, setFuelSurchargePumpClean] = React.useState(0);

    //dropdown data
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [truckSideOptions, setTruckSideOptions] = React.useState<GenericList[]>([]);
    const [truckOptions, setTruckOptions] = React.useState<GenericList[]>([]);
    const [itemTypeGroupOptions, setItemTypeGroupOptions] = React.useState<GenericList[]>([]);
    const [extraItemOptions, setExtraItemOptions] = React.useState<GenericList[]>([]);
    const [frequencyOptions, setFrequencyOptions] = React.useState<GenericList[]>([]);
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [superSafeSupplierOptions, setSuperSafeSupplierOptions] = React.useState<GenericList[]>([]);
    const [powerPoleLocationOptions, setPowerPoleLocationOptions] = React.useState<GenericList[]>([]);
    const [powerPoleDistanceOptions, setPowerPoleDistanceOptions] = React.useState<GenericList[]>([]);
    const [showBracing, setShowBracing] = React.useState(false);

    const [calcPrice, setCalcPrice] = React.useState(false);
    const [calcJobPrice, setCalcJobPrice] = React.useState(false);  //totals amounts to get total price

    React.useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Job/GetScheduleAddData?SubId=' + user.sub + '&jobId=' + props.jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            //fence areas need to load with a selection
            var fenceAreasInitial = res.data.fenceAreas;
            setNewItem(prevState => ({ ...prevState, fenceAreas: fenceAreasInitial }));
            setGateCode(res.data.gateCode);
            setItemOptions(res.data.itemOptions);
            setTruckSideOptions(res.data.truckSideOptions);
            setTruckOptions(res.data.truckOptions);
            setItemTypeGroupOptions(res.data.itemTypeGroupOptions);
            setExtraItemOptions(res.data.extraItemOptions);
            setFrequencyOptions(res.data.frequencyOptions);
            setYesNoOptions(res.data.yesNoOptions);
            setSuperSafeSupplierOptions(res.data.superSafeSupplierOptions);
            setPowerPoleLocationOptions(res.data.powerPoleLocationOptions);
            setPowerPoleDistanceOptions(res.data.powerPoleDistanceOptions);
            setExtraId({ siteOfficeExtraId: res.data.siteOfficeExtraId, lunchRoomExtraId: res.data.lunchRoomExtraId });
            setCalloutFee(res.data.calloutFee);
            setCustomerInvQuoted(res.data.customerInvQuoted);
            setCanSendEmailToCustomer(res.data.canSendEmailToCustomer);
            setEpaLidLocationFeeFormatted(res.data.epaLidLocationFeeFormatted);
            setConfinedSpaceFee({ fee: res.data.confinedSpaceFee, feeFormatted: res.data.confinedSpaceFeeFormatted });
            setTruckHireFee({ fee: res.data.truckHireFee, feeFormatted: res.data.truckHireFeeFormatted });
            setWeekendFee({ fee: res.data.weekendFee, feeFormatted: res.data.weekendFeeFormatted });
            setEmergencyFee({ fee: res.data.emergencyFee, feeFormatted: res.data.emergencyFeeFormatted });
            setChargeFuelSurcharge(res.data.chargeFuelSurcharge);
            setFuelSurchargePumpClean(res.data.fuelSurchargePumpClean);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    React.useEffect(() => {
        if (calcPrice) {
            calculatePrice(newItem, powerPoleQuestions);
        }
    }, [calcPrice]);

    React.useEffect(() => {
        if (calcJobPrice) {
            calculateJobPrice();
        }
    }, [calcJobPrice]);

    const hideModal = () => {
        resetScreen();
        props.hideModal();
    }

    const resetScreen = () => {
        setErrors({});
        setLoading(false);
        setSaveDisabled(false);
        let newItemReset = initNewItem;
        let fenceAreas = newItem.fenceAreas;
        for (var i = 0; i < fenceAreas.length; i++) {
            fenceAreas[i].selected = false;
        }
        newItemReset.fenceAreas = fenceAreas;

        setNewItem(newItemReset);
        setGateCode("");
        setPowerPoleQuestions({
            isUnderground: false,
            superSafeConnecting: -1,
            superSafeSupplierId: -1,
            powerPoleLocationId: -1,
            powerPreviouslyConnected: -1,
            powerAbolished: -1,
            siteLocked: -1,
            clearSightLine: -1,
            pitOnSite: -1,
            pitDistanceId: -1,
            superSafeDiggingTrench: -1
        });
        resetEpaQuestions();
        setItemQuestions({showQuestions: false, itemType: 0});
        setSchedulePickup(props.isEvent);
        setExtas({ tables: 0, chairs: 0 });
    }
    
    const resetEpaQuestions = () => {
        setEpaItemQuestions(prevState => ({
            ...prevState,
            tankLidExposed: -1,
            tankLidLocationKnown: -1,
            tankLidLocation: "",
            truckLargeAccess: -1,
            truckSmallAccess: -1,
            truckAccessToTank: -1,  //within 25m
            steepDriveway: -1,
            hoseMetres: 0,
            sealedDriveway: -1,
            lastService: "",
            waterBoard: "",
            waterAccessOnSite: -1,
            emergencySameDayJob: -1,
            weekendJob: -1
        }));
    }

    const handleSchedulePickup = (e: any) => {
        let schPickup = e.target.checked;
        setSchedulePickup(schPickup);

        if (schPickup) {
            //calculate offhire date using hire period and scheudle date
            let isValidDate = DateHelpers.isValidDate(newItem.scheduleDate);
            if (isValidDate) {
                let newOffHireDate = calcOffhireDate(newItem.scheduleDate);
                setNewItem(prevState => ({ ...prevState, offHireDate: newOffHireDate }));
            }
        } else {
            //clear offhire and pickup dates
            setNewItem(prevState => ({ ...prevState, offHireDate: "", pickupDate: "" }));
        }
    }

    const clearError = (errorName: string) => {
        var errorList = errors;
        delete errorList[errorName];
        setErrors(errorList);
    }

    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);

        setNewItem(prevState => ({ ...prevState, [e.target.name]: e.target.value }));

        if (e.target.name === "scheduleDate" && newItem.itemTypeId != 22 && (props.needsPickup || schedulePickup) && newItem.hirePeriod > 0 && !newItem.installOnly) {
            //calculate offhire date
            let newOffHireDate = calcOffhireDate(e.target.value);
            setNewItem(prevState => ({ ...prevState, offHireDate: newOffHireDate }));
        } else if (e.target.name === "scheduleDate") {
            setNewItem(prevState => ({ ...prevState, offHireDate: "" }));
        }
    }

    const handleNumberChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);
        let value = e.target.value * 1;

        setNewItem(prevState => ({ ...prevState, [e.target.name]: value }));
    }

    const handleFencingStockChange = (e: any) => {
        let updatedValue = e.target.value * 1;

        setNewItem(prevObject => ({
            ...prevObject,
            fencingStock: {
                ...prevObject.fencingStock,
                [e.target.name]: updatedValue
            }
        }));
    }

    const handleFenceBraceChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);

        let value = e.target.value * 1;

        //calculate # braces required if we are installing fencing not a separate brace item
        var fenceBraceQty = 0;
        if (newItem.itemTypeId === 50 && value > 0) {
            var qty = (newItem.quantity / 2.4) / value;
            fenceBraceQty = Math.trunc(qty);
        }
        setNewItem(prevState => ({ ...prevState, [e.target.name]: value, fenceBraceQuantity: fenceBraceQty }));

        if (fenceBraceQty > 0) {
            calculateFenceBracePrice(newItem.customHirePeriod, newItem.hirePeriod, fenceBraceQty);
        } else {
            setNewItem(prevState => ({ ...prevState, fenceBracePrice: 0, fenceBraceOverhireRate: 0 }));
        }
    }

    const handleFenceBraceQtyChange = (e: any) => {
        clearError(e.target.name);

        var value = e.target.value * 1;

        setNewItem(prevState => ({ ...prevState, [e.target.name]: value }));
        if (value > 0) {
            calculateFenceBracePrice(newItem.customHirePeriod, newItem.hirePeriod, value);
        } else {
            setNewItem(prevState => ({ ...prevState, fenceBracePrice: 0, fenceBraceOverhireRate: 0 }));
        }
    }

    const calcOffhireDate = (scheduleDate: string) => {
        if (DateHelpers.isValidDate(scheduleDate)) {
            if (newItem.invoicePeriodTypeId === 2) {
                //add the number of months
                var newDate = DateHelpers.addMonthsToDate(scheduleDate, newItem.hirePeriod);
                //need to remove one day as dates inclusive
                newDate = DateHelpers.addDaysToDate(newDate, -1);
                return newDate;
            } else {
                var days = newItem.customHirePeriod ? (newItem.hirePeriod * 7) - 1 : newItem.hirePeriod - 1;
                var newDate = DateHelpers.addDaysToDate(scheduleDate, days);
                return newDate;
            }
        }
        return "";
    }

    const handleQtyChange = (e: any) => {
        let value = e.target.value * 1;

        let existExtras = newItem.extras;
        existExtras.quantity = value;

        setNewItem(prevState => ({ ...prevState, extras: existExtras }));
        setCalcPrice(true);
    }

    const handleCrossoverQtyChange = (e: any) => {
        let extra: NewJobQuoteItemExtra = {
            itemId: newItem.itemId,
            quantity: e.target.value * 1,
            equipped: false
        };
        setNewItem(prevState => ({ ...prevState, extras: extra }));

        setCalcPrice(true);
    }

    //need to re-calculate the amounts
    const handleCalcChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);
        clearError("amount");
        clearError("overhireRate");
        clearError("quantity");

        let value = e.target.value * 1;
        
        setNewItem(prevState => ({ ...prevState, [e.target.name]: value }));

        if (e.target.name === "hirePeriod" && newItem.scheduleDate && newItem.itemTypeId != 22 && (props.needsPickup || schedulePickup) && value > 0 && !newItem.installOnly) {
            //calculate offhire date
            let newOffHireDate = calcOffhireDate(newItem.scheduleDate);
            setNewItem(prevState => ({ ...prevState, offHireDate: newOffHireDate }));
        } else if (e.target.name === "extraHours" && newItem.itemTypeId === 61) {
            //calculate truck hire
            let truckHire = value * truckHireFee.fee;
            setNewItem(prevState => ({ ...prevState, trenchFee: truckHire }));
        }

        if (newItem.itemTypeId === 61) {
            setCalcJobPrice(true);
        } else {
            setCalcPrice(true);
        }
    }

    const handleCheckboxChange = (e: any, calcPrice: boolean) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);

        //reset callout fee if we are ticking / unticking use per metre pricing
        var chargeCallout = e.target.name === "usePerMetrePricing" ? false : newItem.chargeCalloutFee;

        setNewItem(prevState => ({ ...prevState, [e.target.name]: e.target.checked, chargeCalloutFee: chargeCallout }));

        if (e.target.name == "jobInstalled") {
            //reset to false whenever the job installed tickbox changes
            setNewItem(prevState => ({ ...prevState, jobInvoiced: false, jobInvoicedDate: "" }));
        }

        if (calcPrice) {
            if (newItem.itemTypeId === 61) {
                setCalcJobPrice(true);
            } else {
                setCalcPrice(true);
            }
        } 
    }

    const handleChargeCalloutFeeChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        clearError(e.target.name);

        var itemChargeGst = true;
        var itemOption = itemOptions.find(i => i.id === newItem.itemId);
        if (itemOption) {
            itemChargeGst = itemOption.chargeGst;
        }

        var callout = e.target.checked ? calloutFee : 0;
        var amount = newItem.initialHire + newItem.travel + callout + newItem.trenchFee;

        setNewItem(prevState => ({ ...prevState, [e.target.name]: e.target.checked, chargeGst: itemChargeGst, cleaningFee: callout, amount: amount }));
    }

    const handleAmountChange = (e: any) => {
        clearError(e.target.name);

        let value = e.target.value * 1;
        var initialHireAmt = value - (newItem.cleaningFee * 1) - (newItem.trenchFee * 1) - (newItem.travel * 1);

        setNewItem(prevState => ({ ...prevState, [e.target.name]: value, initialHire: initialHireAmt }));
    }

    //initial hire / cleaning / travel updated - just re-calc the amount field
    const handlePriceChange = (e: any) => {
        clearError(e.target.name);
        var value = e.target.value * 1;
        setNewItem(prevState => ({ ...prevState, [e.target.name]: value }));

        setCalcJobPrice(true);
    }

    const editCustomHirePeriod = (e: any) => {
        var weeklyHirePeriod = newItem.invoicePeriodTypeId === 2 ? newItem.hirePeriod : newItem.hirePeriod / 7;
        setNewItem(prevState => ({ ...prevState, customHirePeriod: true, hirePeriod: weeklyHirePeriod }));
    }

    const handleItemChange = (e: any) => {
        setErrors({});

        var value = e.target.value * 1;
        setNewItem(prevState => ({ ...prevState, itemId: value }));

        var itemOption = itemOptions.find(i => i.id === value);
        if (itemOption) {
            let item = itemOption.name;
            let hirePeriod = itemOption.priceListDefaultDays === 0 ? 1 : itemOption.priceListDefaultDays;
            let defaultTruckId = itemOption.defaultTruckId;
            let typeId = itemOption.typeId;
            let isEws = itemOption.isEws;
            let isEvent = itemOption.isEvent;
            let install = itemOption.installOnly;
            let ext = itemOption.extendable;
            let days = itemOption.priceListDefaultDays;
            let chargeGst = itemOption.chargeGst;
            let invPerId = itemOption.invoicePeriodTypeId;
            let proGrpId = itemOption.productGroupId;
            let fenceAreas = newItem.fenceAreas;
            for (var i = 0; i < fenceAreas.length; i++) {
                fenceAreas[i].selected = false;
            }
            let fenceStock: FencingStock = {
                panels: 0,
                blocks: 0,
                braces: 0,
                clamps: 0,
                pickets: 0,
                signs: 0
            };

            setNewItem(prevState => ({
                ...prevState,
                item: item,
                hirePeriod: hirePeriod,
                defaultHirePeriod: days,
                customHirePeriod: false,
                truckExtraHours: defaultTruckId,
                truckDoorsId: -1,
                itemTypeId: typeId,
                itemTypeGroupId: -1,
                productGroupId: proGrpId,
                isEws: isEws,
                isEvent: isEvent,
                installOnly: install,
                extendable: ext,
                chargeGst: chargeGst,
                frequencyId: -1,
                locationId: -1,
                fenceBraceId: -1,
                fenceBraceQuantity: 0,
                fenceBracePrice: 0,
                fenceBraceOverhireRate: 0,
                fenceAreas: fenceAreas,
                fencingStock: fenceStock,
                invoicePeriodTypeId: invPerId,
                usePerMetrePricing: false,
                chargeCalloutFee: false,
                size: 0,
                extraHours: newItem.itemTypeId != 38 ? 0 : newItem.extraHours,  //reset when item changes as only used for attendant
                cameraLocation: ""
            }));

            if (typeId === 14) {
                //siteOffice
                let newItemExtra: NewJobQuoteItemExtra = {
                    itemId: extraId.siteOfficeExtraId,
                    quantity: 0,
                    equipped: false
                };
                setNewItem(prevState => ({
                    ...prevState,
                    extras: newItemExtra
                }));
            } else if (typeId === 17) {
                //lunch room
                let newItemExtra: NewJobQuoteItemExtra = {
                    itemId: extraId.lunchRoomExtraId,
                    quantity: 0,
                    equipped: false
                };
                setNewItem(prevState => ({
                    ...prevState,
                    extras: newItemExtra
                }));
            } else {
                let newItemExtra: NewJobQuoteItemExtra = {
                    itemId: -1,
                    quantity: 0,
                    equipped: false
                };
                setNewItem(prevState => ({
                    ...prevState,
                    extras: newItemExtra
                }));
            }
            if (typeId === 23) {
                //power pole - check if it is underground
                var itemName: string = e.target.options[e.target.selectedIndex].text;

                //make sure to reset the questions that don't apply
                if (itemName.toLowerCase().includes("underground")) {
                    setPowerPoleQuestions(prevState => ({ ...prevState, isUnderground: true, clearSightLine: -1 }));
                } else {
                    setPowerPoleQuestions(prevState => ({ ...prevState, isUnderground: false, pitOnSite: -1, pitDistanceId: -1, superSafeDiggingTrench: -1 }));
                }               
            }
            if (typeId === 29) {
                //showers automatically get a trolley added
                setNewItem(prevState => ({ ...prevState, trolley: true }));
            } else {
                setNewItem(prevState => ({ ...prevState, trolley: false }));
            }
            if (typeId === 8 || typeId === 50 || typeId === 51 || typeId === 53 || typeId === 54) {
                setNewItem(prevState => ({ ...prevState, quantity: 0 }));   //resetting qty as too easy to leave as 1
            } else {
                setNewItem(prevState => ({ ...prevState, quantity: 1 }));
            }

            if (typeId === 58) {
                var size = item.includes("Treatment Plant") ? 7000 : 3200;
                setNewItem(prevState => ({ ...prevState, size: size }));
            } else if (typeId === 59) {
                setNewItem(prevState => ({ ...prevState, size: 1000 }));
            } else if (typeId === 60) {
                setNewItem(prevState => ({ ...prevState, size: 4000 }));
            }

            if (typeId === 61) {
                //default truck hours to 3 and calculate truck hire
                var truckHire = 3 * truckHireFee.fee;
                //also reset initial hire etc
                setNewItem(prevState => ({ ...prevState, extraHours: 3, trenchFee: truckHire, initialHire: 0, cleaningFee: 0, travel: 0, confinedSpaceAmount: 0, sampleTestingAmount: 0, wasteDisposalAmount: 0 }));
            }

            setOriginalTableChairs(itemOption.name, newItem.itemTypeId);
            resetEpaQuestions();

            //calculate price
            if (typeId === 61) {
                setCalcJobPrice(true);
            } else {
                setCalcPrice(true);
            }
        } else {
            setNewItem(prevState => ({ ...prevState, hirePeriod: 1 }));
        }

    }

    const setOriginalTableChairs = (item: string, typeId: number) => {
        if (typeId === 14 || typeId === 17) {
            //only lunchrooms and site offices have table and chairs
            let isItemLarge = item.includes("6.0");
            let noTables = isItemLarge ? 2 : 1;
            let noChairs = typeId === 14 && isItemLarge ? 4 : typeId === 17 && isItemLarge ? 8 : typeId === 17 ? 4 : 2;
            setExtas({ tables: noTables, chairs: noChairs });
        } else {
            setExtas({ tables: 0, chairs: 0 });
        }
    }

    const setAddress = async (data: any) => {
        setNewItem(prevState => ({
            ...prevState,
            deliveryAddress: data.addressDetails.fullAddress,
            deliveryAddressGooglePlaceId: data.addressDetails.placeId,
            deliveryAddressLatitude: data.addressDetails.latitude,
            deliveryAddressLongitude: data.addressDetails.longitude
        }));
    }

    const updatePowerPoleQuestions = (ppQuestions: PowerPoleQuestions) => {
        setPowerPoleQuestions(ppQuestions);
        //calculate price
        setCalcPrice(true);
    }

    const handleEpaQuestionChange = (fieldName: string, fieldValue: number) => {
        setEpaItemQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (fieldName === "truckAccessToTank" || fieldName === "steepDriveway") {
            setEpaItemQuestions(prevState => ({ ...prevState, hoseMetres: 0 }));
        } else if (fieldName === "tankLidLocationKnown") {
            setEpaItemQuestions(prevState => ({ ...prevState, tankLidLocation: "" }));
        } else if (fieldName === "weekendJob") {
            setEpaItemQuestions(prevState => ({ ...prevState, emergencySameDayJob: -1 }));
        } else if (fieldName === "tankLidExposed") {
            setEpaItemQuestions(prevState => ({ ...prevState, tankLidLocationKnown: -1, tankLidLocation: "" }));
        }
    }

    const handleEpaQuestionTextChange = (fieldName: string, fieldValue: string) => {
        setEpaItemQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const handleEpaInvoiceDescriptionChange = (updatedInvoiceDescription: string) => {
        //not used
    }

    const handleIncludeGreaseTrapChange = (updatedIncGreaseTrap: boolean) => {
        setNewItem(prevState => ({ ...prevState, trolley: updatedIncGreaseTrap }));
    }

    const updateSaveDisabled = (saveDisUpdated: boolean) => {
        setSaveDisabled(saveDisUpdated);
    }

    const saveEpa = () => {
        if (!epaItemQuestions.tankLidExposed) {
            setNewItem(prevState => ({ ...prevState, extraHours: 0.5 }));
        } else if (newItem.itemTypeId != 61) {
            setNewItem(prevState => ({ ...prevState, extraHours: 0 }));
        }
        if (epaItemQuestions.weekendJob) {
            setNewItem(prevState => ({ ...prevState, cleaningFee: weekendFee.fee }));
        } else if (epaItemQuestions.emergencySameDayJob) {
            setNewItem(prevState => ({ ...prevState, cleaningFee: emergencyFee.fee }));
        } else {
            setNewItem(prevState => ({ ...prevState, cleaningFee: 0 }));
        }
        if (epaItemQuestions.confinedSpace) {
            setNewItem(prevState => ({ ...prevState, confinedSpaceAmount: confinedSpaceFee.fee }));
        } else {
            setNewItem(prevState => ({ ...prevState, confinedSpaceAmount: 0 }));
        }
        if (chargeFuelSurcharge && fuelSurchargePumpClean > 0 && newItem.itemTypeId === 61) {
            //need to add a fuel surcharge to EPA Other jobs
            setNewItem(prevState => ({ ...prevState, fuelSurcharge: fuelSurchargePumpClean }));
        }

        setItemQuestions({ showQuestions: false, itemType: 0 });
        setSaveDisabled(false);

        //calculate price
        if (newItem.itemTypeId === 61) {
            setCalcJobPrice(true);
        } else {
            setCalcPrice(true);
        }
    }

    const updateFenceArea = (checked: boolean, itemIndex: number) => {
        clearError("fenceQuestions");
        clearError("fenceArea");
        let fenceAreasInitial = [...newItem.fenceAreas];
        let fenceAreaInitial = fenceAreasInitial[itemIndex];
        fenceAreaInitial.selected = checked;
        if (fenceAreaInitial.id === 1) {
            //full boundry selected so untick the others
            let otherAreas = fenceAreasInitial.filter(f => f.id != 1 && f.selected);
            for (var i = 0; i < otherAreas.length; i++) {
                otherAreas[i].selected = false;
            }
        }
        setNewItem(prevState => ({ ...prevState, fenceAreas: fenceAreasInitial }));
    }

    const updateGateCode = (newGateCode: string) => {
        setNewItem(prevState => ({ ...prevState, gateCode: newGateCode }));
    }

    const calculatePrice = async (updatedItem: NewItem, ppQuestions: PowerPoleQuestions) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var ssDiggingTrench = powerPoleQuestions.superSafeDiggingTrench === 1;
        var chargeTravel = true;    //set all to true for now
        var hirePeriod = updatedItem.customHirePeriod && updatedItem.invoicePeriodTypeId === 1 ? updatedItem.hirePeriod * 7 : updatedItem.hirePeriod;

        var totalSteps = updatedItem.itemTypeId === 55 ? updatedItem.quantity : 0;
        var incStepsTravel = totalSteps > 0;
        var chargeWeekendFee = epaItemQuestions.weekendJob === 1;
        var chargeEmergencyFee = epaItemQuestions.emergencySameDayJob === 1;

        //calculate totals
        axios.get('api/Job/CalculateNewJobPrice?SubId=' + user.sub + '&JobId=' + props.jobId + '&CustomerId=' + props.customerId + "&Duration=" + props.siteAddressDuration + "&ItemId=" + updatedItem.itemId + "&JobItemTypeId=" + updatedItem.itemTypeId + "&ProductGroupId=" + updatedItem.productGroupId + "&IsEws=" + updatedItem.isEws + "&IsEvent=" + updatedItem.isEvent + "&HirePeriod=" + hirePeriod + "&UseTyabbPricelist=false&UsePerMetrePricing=" + updatedItem.usePerMetrePricing + "&FrequencyId=" + updatedItem.frequencyId + "&LocationId=" + updatedItem.locationId + "&Size=" + updatedItem.size + "&ExtraHours=" + updatedItem.extraHours + "&Quantity=" + updatedItem.quantity + "&ChargeGst=" + updatedItem.chargeGst + "&TruckExtraHours=" + updatedItem.truckExtraHours + "&ExtraItemId=" + updatedItem.extras.itemId + "&ExtraItemQuantity=" + updatedItem.extras.quantity + "&IncludeTrolley=" + updatedItem.trolley + "&ChargeTravel=" + chargeTravel + "&PowerPoleSupplierId=" + ppQuestions.superSafeSupplierId + "&PowerPoleDistanceId=" + ppQuestions.pitDistanceId + "&PowerPoleSSDiggingTrench=" + ssDiggingTrench + "&TotalSteps=" + totalSteps + "&IncludeStepsTravel=" + incStepsTravel + "&ChargeWeekendJob=" + chargeWeekendFee + "&ChargeEmergencyJob=" + chargeEmergencyFee + "&ChargeFuelSurcharge=" + chargeFuelSurcharge, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setCalcPrice(false);
            var cleaning = updatedItem.chargeCalloutFee ? calloutFee : res.data.cleaning;
            var amount = updatedItem.chargeCalloutFee ? res.data.amount + calloutFee : res.data.amount;

            setNewItem(prevState => ({
                ...prevState,
                initialHire: res.data.initialHire,
                travel: res.data.travel,
                cleaningFee: cleaning,
                trenchFee: res.data.trenchFee,
                fuelSurcharge: res.data.fuelSurcharge,
                confinedSpaceAmount: 0,
                sampleTestingAmount: 0,
                wasteDisposalAmount: 0,
                amount: amount,
                overhireRate: res.data.overhireRate
            }));

            if (newItem.fenceBraceQuantity > 0) {
                calculateFenceBracePrice(newItem.customHirePeriod, newItem.hirePeriod, newItem.fenceBraceQuantity);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const calculateFenceBracePrice = async (customHirePeriod: boolean, itemHirePeriod: number, fenceBraceQuantity: number) => {
        const token = await authService.getAccessToken();

        //calculate fence braces price
        var hirePeriod = customHirePeriod && newItem.invoicePeriodTypeId === 1 ? itemHirePeriod * 7 : itemHirePeriod;
        axios.get('api/Job/CalculateFenceBracePrice?JobId=' + props.jobId + '&CustomerId=' + props.customerId + "&HirePeriod=" + hirePeriod + "&BraceQuantity=" + fenceBraceQuantity, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setNewItem(prevState => ({
                ...prevState,
                fenceBracePrice: res.data.amount,
                fenceBraceOverhireRate: res.data.overhireRate
            }));
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const calculateJobPrice = () => {
        //total all fields and set total price
        let itemAmount = newItem.initialHire + newItem.travel + newItem.cleaningFee + newItem.trenchFee + newItem.confinedSpaceAmount + newItem.sampleTestingAmount + newItem.wasteDisposalAmount + newItem.fuelSurcharge;
        let jobPrice = parseFloat(itemAmount.toFixed(2));
        setNewItem(prevState => ({ ...prevState, amount: jobPrice }));
        setCalcJobPrice(false);
    }

    const validate = () => {
        let fields = newItem;
        let errorList: any = {};
        let formIsValid = true;
        
        if (fields.itemId <= 0) {
            formIsValid = false;
            errorList["itemId"] = "Item is required";
        }

        //only need to set the scheduler for manual / service fee / callouts
        if (fields.itemTypeId === 22 && fields.itemTypeGroupId <= 0) {
            formIsValid = false;
            errorList["itemTypeGroupId"] = "Scheduler is required";
        }

        if (fields.hirePeriod <= 0) {
            formIsValid = false;
            errorList["hirePeriod"] = "Hire Period is required";
        }

        var itemDetails = itemOptions.find(i => i.id === fields.itemId);
        let isElite = false;
        if (itemDetails != null) {
            isElite = itemDetails.name.toLowerCase().includes("elite");
        }

        if (fields.isEws && fields.truckExtraHours <= 0) {
            formIsValid = false;
            errorList["truckId"] = "Truck is required";
        } else if (isElite && fields.truckExtraHours != 1) {
            errorList["truckId"] = " Elite Units MUST use Tilt Truck";
            formIsValid = false;
        }

        if (fields.isEws && fields.truckDoorsId <= 0) {
            formIsValid = false;
            errorList["truckDoorsId"] = "Doors Facing is required";
        }

        if (fields.itemTypeId === 19 && !fields.cameraLocation) {
            formIsValid = false;
            errorList["cameraLocation"] = "Camera Location is required";
        }

        if (fields.productGroupId === 6 && fields.size <= 0) {
            formIsValid = false;
            errorList["size"] = "Tank Size is required";
        }

        if (!fields.scheduleDate) {
            formIsValid = false;
            errorList["scheduleDate"] = "Schedule Date is required";
        } else if (!DateHelpers.isValidDate(fields.scheduleDate)) {
            formIsValid = false;
            errorList["scheduleDate"] = "Valid Schedule Date is required";
        }

        if ((!fields.installOnly && props.needsPickup && (!fields.pickupDate || !fields.offHireDate)) || fields.pickupDate || fields.offHireDate) {
            if (!fields.pickupDate) {
                formIsValid = false;
                errorList["pickupDate"] = "Pickup Date is required";
            } else if (!DateHelpers.isValidDate(fields.pickupDate)) {
                formIsValid = false;
                errorList["pickupDate"] = "Valid Pickup Date is required";
            } else if (!DateHelpers.compareDate(fields.scheduleDate, fields.pickupDate)) {
                formIsValid = false;
                errorList["offHireDate"] = "Pickup Date >= On Hire Date";
            }

            if (!fields.offHireDate) {
                formIsValid = false;
                errorList["offHireDate"] = "Off Hire Date is required";
            } else if (!DateHelpers.isValidDate(fields.offHireDate)) {
                formIsValid = false;
                errorList["offHireDate"] = "Valid Off Hire Date is required";
            } else if (!DateHelpers.compareDate(fields.offHireDate, fields.pickupDate)) {
                formIsValid = false;
                errorList["offHireDate"] = "Offhire Date <= Pickup Date";
            }
        }

        if (fields.itemTypeId === 38 && (!fields.extraHours || fields.extraHours <= 0)) {
            formIsValid = false;
            errorList["extraHours"] = "Hours is required";
        }

        if (fields.itemTypeId === 50) {
            var fenceAreas = fields.fenceAreas.filter(f => f.selected);
            if (fenceAreas.length === 0) {
                formIsValid = false;
                errorList["fenceArea"] = "Fence Area is required";
            }
        }

        if (fields.itemTypeId === 50 && fields.jobInstalled && fields.fencingStock.panels < 1) {
            formIsValid = false;
            errorList["panels"] = "Panels are required";
        }

        if (fields.itemTypeId === 51 && fields.fenceBraceId <= 0) {
            formIsValid = false;
            errorList["fenceBraceId"] = "Bracing is required";
        }

        if (!fields.quantity || fields.quantity <= 0) {
            formIsValid = false;
            errorList["quantity"] = "Quantity is required";
        }

        if (fields.itemTypeId === 61) {
            //EPA jobs don't use a pricelist so need to make sure they enter the inital service price
            var initialHire = fields.initialHire * 1;
            if (initialHire <= 0) {
                formIsValid = false;
                errorList["initialHire"] = "Service Amount is required";
            }

            if (fields.extraHours <= 0) {
                formIsValid = false;
                errorList["extraHours"] = "Truck Hours are required";
            }

            if (fields.trenchFee <= 0) {
                formIsValid = false;
                errorList["trenchFee"] = "Truck Hire is required";
            }
        }

        var amount = fields.amount * 1;
        var canBeZero = (fields.itemTypeId === 50 || fields.itemTypeId === 51) && customerInvQuoted;    //Simonds can add Fence or Fence Braces at a later date but won't get charged as incl in original invoice
        if (amount <= 0 && !canBeZero) {
            formIsValid = false;
            errorList["amount"] = "Amount is required";
        }

        //Callouts, Event Jobs don't need an overhire rate
        var overhireRate = fields.overhireRate * 1;
        if (fields.extendable && !props.isEvent && overhireRate <= 0 && !canBeZero) {
            formIsValid = false;
            errorList["overhireRate"] = "Additional Hire Rate is required";
        }

        setErrors(errorList);

        return formIsValid;
    }

    const validatePowerPoleQuestions = () => {
        var errorList: { [key: string]: string } = {};
        var formIsValid = true;

        //power pole validation
        if (powerPoleQuestions.superSafeConnecting < 0) {
            formIsValid = false;
            errorList["superSafeConnecting"] = "Super Safe Connecting Electricity is required";
        }

        if (powerPoleQuestions.superSafeConnecting === 1 && powerPoleQuestions.superSafeSupplierId < 0) {
            formIsValid = false;
            errorList["superSafeSupplierId"] = "Distributor is required";
        }

        if (powerPoleQuestions.powerPoleLocationId < 0) {
            formIsValid = false;
            errorList["powerPoleLocationId"] = "Power Pole Location is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected < 0) {
            formIsValid = false;
            errorList["powerPreviouslyConnected"] = "Power to the property is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected === 1 && powerPoleQuestions.powerAbolished < 0) {
            formIsValid = false;
            errorList["powerAbolished"] = "Power Abolished is required";
        }

        if (powerPoleQuestions.siteLocked < 0) {
            formIsValid = false;
            errorList["siteLocked"] = "Is Site Locked is required";
        }

        if (!powerPoleQuestions.isUnderground && powerPoleQuestions.clearSightLine < 0) {
            formIsValid = false;
            errorList["clearSightLine"] = "Clear Line of Sight is required";
        }

        if (powerPoleQuestions.isUnderground) {
            if (powerPoleQuestions.pitOnSite < 0) {
                formIsValid = false;
                errorList["pitOnSite"] = "Pit on site is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.pitDistanceId < 0) {
                formIsValid = false;
                errorList["pitDistanceId"] = "Distance between pit and pole is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.superSafeDiggingTrench < 0) {
                formIsValid = false;
                errorList["superSafeDiggingTrench"] = "Super Safe digging trench is required";
            }
        }
        setErrors(errorList);

        return formIsValid;
    }

    const validateEpaQuestions = () => {
        var errorList: { [key: string]: string } = {};
        var formIsValid = true;

        //all items need to answer this question
        if (epaItemQuestions.weekendJob < 0) {
            formIsValid = false;
            errorList["epaQuestions"] = "Epa Questions need to be answered";
            setErrors(errorList);
        }

        return formIsValid;
    }

    const schedule = (e: any) => {
        e.preventDefault();

        if (validate()) {
            if (saveDisabled) {
                return;
            }
            setSaveDisabled(true);

            if (newItem.itemTypeId === 23 || newItem.productGroupId === 6) {
                if (newItem.itemTypeId === 23 ? validatePowerPoleQuestions() : validateEpaQuestions()) {
                    //make sure they have answered all the powerpole questions
                    scheduleNewItem();
                } else {
                    var questionsText = newItem.itemTypeId === 23 ? "power pole" : "epa";
                    toast.error("Please answer " + questionsText + " questions before saving!");
                }
            } else {
                scheduleNewItem();
            }
        } else {
            toast.error("Please fix validation errors on screen before saving!");
        }
    }

    //save changes
    const scheduleNewItem = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var newJobItem = newItem;
        if (newJobItem.customHirePeriod && newJobItem.invoicePeriodTypeId === 1) {
            //need to change from weeks to days for saving
            newJobItem.hirePeriod = newJobItem.hirePeriod * 7;
        }

        var addNewJobItem = {
            subId: user.sub,
            jobId: props.jobId,
            newItem: newJobItem,
            gateCode: gateCode,
            powerPoleQuestions: powerPoleQuestions,
            epaQuestions: epaItemQuestions
        };

        //save
        axios.post('api/Job/AddNewScheduleItem', addNewJobItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("New Item Scheduled Successfully");
            if (newItem.sendEmailToCustomer) {
                sendReport(user.sub);
            } else {
                resetScreen();
                props.save();
            }
        })
        .catch(error => {
            setSaveDisabled(false);
            toast.error(error.message);
        });
    }

    const sendReport = async (subId: string) => {
        const token = await authService.getAccessToken();

        //get the data
        //set param1 to 0 as that is for quote version id which isn't needed here as there is only one version of the quote
        axios.get('api/Report/GetJobRequestData?jobId=' + props.jobId + "&reportName=JobRequest&param1=0", {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                //Run Job Request / Quote Request report
                var report = new Stimulsoft.Report.StiReport();
                var reportFile = Stimulsoft.System.IO.File.getFile("reports/" + res.data.reportFileName);
                report.load(reportFile);
                report.dictionary.dataStore.clear();

                var reportData = JSON.stringify(res.data.reportData);
                //Dataset created
                var dataSet = new Stimulsoft.System.Data.DataSet("data");
                dataSet.readJson(reportData)
                report.regData(dataSet.dataSetName, "", dataSet);
                report.reportName = res.data.reportName;
                report.dictionary.connect(false);
                report.dictionary.synchronize();

                //add relationships between data
                for (var i = 0; i < res.data.reportRelationships.length; i++) {
                    var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(res.data.reportRelationships[i].sourceName, res.data.reportRelationships[i].name, res.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(res.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(res.data.reportRelationships[i].childTableName), [res.data.reportRelationships[i].parentFieldName], [res.data.reportRelationships[i].childFieldName]);
                    report.dictionary.relations.add(dataRelation);
                    report.dictionary.synchronize();
                }
                report.render();

                //Download the report to a pdf file
                var service = new Stimulsoft.Report.Export.StiPdfExportService();
                var stream = new Stimulsoft.System.IO.MemoryStream();
                var settings = new Stimulsoft.Report.Export.StiPdfExportSettings();
                service.exportTo(report, stream, settings);
                var bytes = stream.toArray();
                var fileData = [new Uint8Array(bytes)];
                var fileName = "Order Confirmation";
                var saveFile = new File(fileData, fileName, { type: 'application/pdf' });

                //Send the report to the back-end to send the email to the customer
                const data = new FormData()
                data.append('jobId', props.jobId.toString());
                data.append('sendEmail', newItem.sendEmailToCustomer.toString());
                data.append('subId', subId);
                data.append('report', saveFile);

                //email confirmation, T&C's to customer if email box ticked otherwise just save files 
                //upload any files added to the job
                axios.post('api/Job/SendConfirmationEmail', data, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    if (res.data.isError) {
                        var errors = res.data.messages as any[];
                        errors.map(function (error: any) {
                            toast.error(error.content);
                        });
                    } else {
                        resetScreen();
                        props.save();
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            }
        });

    }

    let cleaningConnectionHeading = newItem.itemTypeId === 23 ? "Connection" : newItem.usePerMetrePricing || newItem.productGroupId === 6 ? "Callout Fee" : "Cleaning";
    const isFullBoundry = newItem.fenceAreas.find(f => f.id === 1)?.selected;

    const ppQuestions = <NewJobPricelistPowerPoleQuestionsData isReadOnly={false} isUnderground={powerPoleQuestions.isUnderground} powerPoleQuestions={powerPoleQuestions} gateCode={gateCode} yesNoOptions={yesNoOptions} powerPoleLocationOptions={powerPoleLocationOptions} powerPoleDistanceOptions={powerPoleDistanceOptions} superSafeSupplierOptions={superSafeSupplierOptions} errors={errors} updateGateCode={updateGateCode} updatePowerPoleQuestions={updatePowerPoleQuestions} />
    const epaQuestions = <JobQuoteEpaQuestionsData isReadOnly={false} itemTypeId={newItem.itemTypeId} invoiceDescription="" showInvoiceDescription={false} includeGreaseTrap={newItem.trolley} epaLidLocationFeeFormatted={epaLidLocationFeeFormatted} weekendFeeFormatted={weekendFee.feeFormatted} emergencyFeeFormatted={emergencyFee.feeFormatted} epaQuestions={epaItemQuestions} yesNoOptions={yesNoOptions} showBack={true} saveDisabled={saveDisabled} updateNumber={handleEpaQuestionChange} updateText={handleEpaQuestionTextChange} updateInvoiceDescription={handleEpaInvoiceDescriptionChange} updateIncGreaseTrap={handleIncludeGreaseTrapChange} updateSaveDisabled={updateSaveDisabled} save={saveEpa} ></JobQuoteEpaQuestionsData>
    const googleContents = <GooglePlaces addressId="autocomplete" addressLabel={"Deivery Address - if different from site address"} placeholder={newItem.deliveryAddress} className="input-group" useLargeLabel={true} afterValid={setAddress} />

    const renderDetails = (
        <div className="static-modal">
            <Modal size='lg' show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Schedule New Job</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <div className={itemQuestions.showQuestions && itemQuestions.itemType === 1 ? "" : "hidden"}>
                        {ppQuestions}
                    </div>
                    <div className={itemQuestions.showQuestions && itemQuestions.itemType === 2 ? "" : "hidden"}>
                        {epaQuestions}
                    </div>
                    <div className={itemQuestions.showQuestions ? "hidden" : ""}>
                        <label className="input-group" htmlFor="itemId">
                            <span className="label label-large">Item</span>
                            <select className="select jobschedule-newjob-item--width" id="itemId" name="itemId" value={newItem.itemId} onChange={(e) => handleItemChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {itemOptions.map(item =>
                                    props.isEvent || (!props.isEvent && item.typeId != 33 && item.typeId != 42) ?
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                        : ""
                                )};
                            </select>
                        </label>
                        <span className={errors["itemId"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["itemId"]}</span>
                        <span className={newItem.itemTypeId === 52 ? "label errors marginLeftLabelLarge" : "hidden"}>Fencing Extras DO NOT go into overhire! Use for gate wheels or extra pickets, clamps if no panels required</span>
                        <span className={newItem.itemTypeId === 52 ? "label errors marginLeftLabelLarge" : "hidden"}>Use Fencing Standard with BTM pricing for extra Panels</span>

                        <label className={newItem.itemTypeId === 22 ? "input-group" : "hidden"} htmlFor="itemTypeGroupId">
                            <span className="label label-large">Scheduler</span>
                            <select className="select  modal-inputsize200" id="itemTypeGroupId" name="itemTypeGroupId" value={newItem.itemTypeGroupId} onChange={(e) => handleChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {itemTypeGroupOptions.map(item =>
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={errors["itemTypeGroupId"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["itemTypeGroupId"]}</span>

                        <label className={(newItem.hirePeriod === 1 && !newItem.customHirePeriod) || newItem.isEvent ? "hidden" : "input-group"} htmlFor="hirePeriod">
                            <span className="label label-large">Hire Period {newItem.invoicePeriodTypeId === 2 ? "(months)" : "(weeks)"}</span>
                            <select className={newItem.customHirePeriod ? "hidden" : "select modal-inputsize200"} id="hirePeriod" name="hirePeriod" value={newItem.hirePeriod} onChange={(e) => handleCalcChange(e)} disabled={newItem.itemTypeId === 53 }>
                                <option hidden defaultValue="-1"></option>
                                <option hidden={newItem.defaultHirePeriod > 1} value={1}>1 Day</option>
                                <option hidden={newItem.defaultHirePeriod > 7 || newItem.invoicePeriodTypeId === 2} value={7}>1 Week</option>
                                <option hidden={newItem.defaultHirePeriod > 14 || newItem.invoicePeriodTypeId === 2} value={14}>2 Weeks</option>
                                <option hidden={newItem.defaultHirePeriod > 21 || newItem.invoicePeriodTypeId === 2} value={21}>3 Weeks</option>
                                <option hidden={newItem.defaultHirePeriod > 28 || newItem.invoicePeriodTypeId === 2} value={28}>4 Weeks</option>
                                <option hidden={newItem.defaultHirePeriod > 35 || newItem.invoicePeriodTypeId === 2} value={35}>5 Weeks</option>
                                <option hidden={newItem.itemTypeId === 19 || newItem.itemTypeId === 23 || newItem.itemTypeId === 28 || newItem.itemTypeId === 27 || newItem.invoicePeriodTypeId === 2} value={42}>6 Weeks</option>
                                <option hidden={newItem.itemTypeId === 19 || newItem.itemTypeId === 23 || newItem.itemTypeId === 25 || newItem.itemTypeId == 26 || newItem.itemTypeId === 28 || newItem.itemTypeId === 27 || newItem.invoicePeriodTypeId === 2 } value={56}>8 Weeks</option>
                                <option hidden={newItem.itemTypeId === 19 || newItem.itemTypeId === 23 || newItem.itemTypeId === 28 || newItem.itemTypeId === 27 || newItem.invoicePeriodTypeId === 2} value={70}>10 Weeks</option>
                                <option hidden={newItem.invoicePeriodTypeId === 2} value={91}>13 Weeks</option>
                                <option hidden={newItem.itemTypeId === 23 || newItem.invoicePeriodTypeId === 2} value={98}>14 Weeks</option>
                                <option hidden={newItem.itemTypeId === 23 || newItem.invoicePeriodTypeId === 2} value={112}>16 Weeks</option>
                                <option value={newItem.invoicePeriodTypeId === 2 ? 6 : 182}>{newItem.invoicePeriodTypeId === 2 ? "6 Months" : "26 Weeks"}</option>
                                <option hidden={newItem.itemTypeId != 25} value={280}>40 Weeks</option>
                                <option hidden={newItem.itemTypeId === 53} value={newItem.invoicePeriodTypeId === 2 ? 12 : 364}>{newItem.invoicePeriodTypeId === 2 ? "12 Months" : "52 Weeks"}</option>
                            </select>
                            <input type='number' min="0" step="1" className={newItem.customHirePeriod ? "form-control allocate__size100 modal-inputsize200 textalignright" : "hidden"} id="hirePeriod" name="hirePeriod" value={newItem.hirePeriod} onChange={(e) => handleCalcChange(e)} />
                            <span className={newItem.customHirePeriod || newItem.itemId < 1 || newItem.itemTypeId === 53 || newItem.isEvent ? "hidden" : "tooltipitem tooltipitem__right"}><i className="fa-solid fa-pen-to-square marginLeft10" onClick={(e) => editCustomHirePeriod(e)}></i>
                                <span className="tooltipitemtext tooltipitemtext__right">Enter Hire Period in {newItem.invoicePeriodTypeId === 2 ? "Months" : "Weeks"}</span>
                            </span>
                        </label>
                        <span className={errors["hirePeriod"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["hirePeriod"]}</span>

                        <label className={newItem.itemTypeId === 22 || newItem.itemTypeId === 61 ? "hidden" : "input-group"} htmlFor="jobInstalled">
                            <span className="label label-large">{newItem.itemTypeId === 54 ? "Re-instate Already Completed?" : "Item Already on Site?"}</span>
                            <input className="checkbox" type="checkbox" name="jobInstalled" checked={newItem.jobInstalled} onChange={(e) => handleCheckboxChange(e, false)} ></input>
                        </label>
                        <label className={newItem.jobInstalled ? "input-group" : "hidden"} htmlFor="jobInvoiced">
                            <span className="label label-large">Job Invoiced?</span>
                            <input className="checkbox" type="checkbox" name="jobInvoiced" checked={newItem.jobInvoiced} onChange={(e) => handleCheckboxChange(e, false)} ></input>
                        </label>

                        <label className={newItem.jobInstalled || newItem.itemTypeId != 54 ? "hidden" : "input-group"} htmlFor="createManualPickup">
                            <span className="label label-large">Create Part Pickup?</span>
                            <input className="checkbox" type="checkbox" name="createManualPickup" checked={newItem.createManualPickup} onChange={(e) => handleCheckboxChange(e, false)} ></input>
                        </label>

                        <label className={newItem.jobInvoiced ? "input-group" : "hidden"} htmlFor="jobInvoicedDate">
                            <span className="label label-large">Invoiced Up To</span>
                            <input className="input modal-inputsize200" type="date" name="jobInvoicedDate" value={newItem.jobInvoicedDate} onChange={(e) => handleChange(e)} ></input>
                        </label>

                        <label className={newItem.jobInstalled ? "hidden" : "input-group"} htmlFor="sendEmailToCustomer">
                            <span className="label label-large label--setsizesm">Send Confirmation Email</span>
                            <input className="checkbox" type="checkbox" id="sendEmailToCustomer" name="sendEmailToCustomer" checked={newItem.sendEmailToCustomer} onChange={(e) => handleCheckboxChange(e, false)} disabled={!canSendEmailToCustomer}></input>
                        </label>

                        <label className={newItem.itemTypeId === 50 ? "input-group" : "hidden"} htmlFor="usePerMetrePricing">
                            <span className="label label-large">Use Per Metre Pricing?</span>
                            <input className="checkbox" type="checkbox" name="usePerMetrePricing" checked={newItem.usePerMetrePricing} onChange={(e) => handleCheckboxChange(e, true)} ></input>
                        </label>

                        <label className={newItem.itemTypeId === 50 && newItem.usePerMetrePricing ? "input-group" : "hidden"} htmlFor="chargeCalloutFee">
                            <span className="label label-large">Charge Callout Fee?</span>
                            <input className="checkbox" type="checkbox" name="chargeCalloutFee" checked={newItem.chargeCalloutFee} onChange={(e) => handleChargeCalloutFeeChange(e)} ></input>
                        </label>

                        <div className={newItem.jobInstalled && newItem.itemTypeId === 50 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="panels">
                                <span className="label label-large">Panels</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="panels" name="panels" value={newItem.fencingStock.panels} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                            <span className={errors["panels"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["panels"]}</span>

                            <label className="input-group" htmlFor="blocks">
                                <span className="label label-large">Blocks</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="blocks" name="blocks" value={newItem.fencingStock.blocks} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="clamps">
                                <span className="label label-large">Clamps</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="clamps" name="clamps" value={newItem.fencingStock.clamps} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="pickets">
                                <span className="label label-large">Pickets</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="pickets" name="pickets" value={newItem.fencingStock.pickets} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="signs">
                                <span className="label label-large">Signs</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="signs" name="signs" value={newItem.fencingStock.signs} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="braces">
                                <span className="label label-large">Braces</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="braces" name="braces" value={newItem.fencingStock.braces} onChange={(e) => handleFencingStockChange(e)} />
                            </label>
                        </div>

                        <label className={newItem.itemId === 81 ? "input-group" : "hidden"} htmlFor="locationId">
                            <span className="label label-large">Location</span>
                            <select className="select modal-inputsize200" id="locationId" name="locationId" value={newItem.locationId} onChange={(e) => handleCalcChange(e)}>
                                <option defaultValue="-1"></option>
                                <option key={1} value={1}>In Yard</option>
                                <option key={2} value={2}>On Site</option>
                            </select>
                        </label>

                        <label className={newItem.productGroupId === 6 ? "input-group" : "hidden"} htmlFor="size">
                            <span className="label label-large">Size</span>
                            <input type="number" min="1" step="1" className="input modal-inputsize200" id="size" name="size" value={newItem.size} onChange={(e) => handleCalcChange(e)} />
                        </label>
                        <span className={errors["size"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["size"]}</span>


                        <label className={!newItem.item.toLowerCase().includes("sewer connect") && (newItem.itemTypeId === 25 || newItem.itemTypeId === 26 || (newItem.itemTypeId > 57 && newItem.itemTypeId < 61)) ? "input-group" : "hidden"} htmlFor="frequencyId">
                            <span className="label label-large">Frequency</span>
                            <select className="select modal-inputsize200" id="frequencyId" name="frequencyId" value={newItem.frequencyId} onChange={(e) => handleCalcChange(e)}>
                                <option defaultValue="-1"></option>
                                {frequencyOptions.map(freq =>
                                    <option key={freq.id} hidden={(newItem.itemTypeId === 59 && (freq.id < 4 || freq.id > 8)) || (newItem.itemTypeId === 58 && freq.id < 9) || ((newItem.itemTypeId < 58 || newItem.itemTypeId > 59) && freq.id > 3)} value={freq.id}>{freq.name}</option>
                                )};
                            </select>
                        </label>

                        <label className={newItem.itemTypeId === 25 || newItem.itemTypeId === 29 || newItem.itemTypeId === 44 || newItem.itemTypeId === 58 ? "input-group" : "hidden"} htmlFor="trolley">
                            <span className="label label-large">{newItem.itemTypeId === 58 ? "Include Grease Trap?" : "Include Trolley?"}</span>
                            <input className="checkbox" type="checkbox" id="trolley" name="trolley" checked={newItem.trolley} onChange={(e) => handleCheckboxChange(e, true)}></input>
                        </label>

                        <label className={newItem.itemTypeId === 22 || newItem.itemTypeId === 26 || newItem.itemTypeId === 50 || newItem.itemTypeId === 51 || newItem.itemTypeId === 61 ? "hidden" : "input-group"} htmlFor="unitNumber">
                            <span className="label label-large">Unit Number</span>
                            <input className="input" type="text" maxLength={100} id="unitNumber" name="unitNumber" value={newItem.unitNumber} onChange={(e) => handleChange(e)}></input>
                        </label>

                        <label className={newItem.itemTypeId === 19 ? "input-group" : "hidden"} htmlFor="cameraLocation">
                            <span className="label label-large">Camera Location</span>
                            <input className="input" type="text" maxLength={200} id="cameraLocation" name="cameraLocation" value={newItem.cameraLocation} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["cameraLocation"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["cameraLocation"]}</span>

                        <label className="input-group" htmlFor="purchaseOrderNumber">
                            <span className="label label-large">Purchase Order Number</span>
                            <input className="input" type="text" maxLength={30} id="purchaseOrderNumber" name="purchaseOrderNumber" value={newItem.purchaseOrderNumber} onChange={(e) => handleChange(e)}></input>
                        </label>

                        <div className={props.isEvent || newItem.isEvent ? "" : "hidden"}>
                            {googleContents}
                        </div>

                        <div className={newItem.isEvent || newItem.itemTypeId === 52 || newItem.itemTypeId === 22 || newItem.itemTypeId === 61 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="invoiceDescription">
                                <span className="label label-large">Invoice Desc - if different from Item name</span>
                                <input className="input" type="text" maxLength={30} id="invoiceDescription" name="invoiceDescription" value={newItem.invoiceDescription} onChange={(e) => handleChange(e)}></input>
                            </label>
                        </div>

                        <div className={newItem.isEws ? "" : "hidden"}>
                            <label className="input-group" htmlFor="truckExtraHours">
                                <span className="label label-large">Truck</span>
                                <select className="select modal-inputsize200" id="truckExtraHours" name="truckExtraHours" value={newItem.truckExtraHours} onChange={(e) => handleCalcChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {truckOptions.map(truck =>
                                        <option key={truck.id} value={truck.id}>{truck.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["truckId"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["truckId"]}</span>

                            <label className="input-group" htmlFor="truckDoorsId">
                                <span className="label label-large">Doors Facing?</span>
                                <select className="select modal-inputsize200" id="truckDoorsId" name="truckDoorsId" value={newItem.truckDoorsId} onChange={(e) => handleChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {truckSideOptions.map(ts =>
                                        <option key={ts.id} value={ts.id}>{ts.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["truckDoorsId"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["truckDoorsId"]}</span>
                        </div>
                        
                        <label className="input-group" htmlFor="scheduleDate">
                            <span className="label label-large">Schedule Date</span>
                            <input type='date' id="scheduleDate" name="scheduleDate" className='input modal-inputsize200' value={newItem.scheduleDate} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["scheduleDate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["scheduleDate"]}</span>

                        <label className={newItem.installOnly ? "hidden" : "input-group"} htmlFor="schedulePickup">
                            <span className="label label-large label--setsizesm">Schedule Pickup?</span>
                            <input className="checkbox" type="checkbox" id="schedulePickup" name="schedulePickup" checked={schedulePickup} onChange={(e) => handleSchedulePickup(e)} disabled={props.needsPickup}></input>
                        </label>

                        <div className={schedulePickup && newItem.itemTypeId != 22 && !newItem.installOnly ? "" : "hidden" }>
                            <label className="input-group" htmlFor="offHireDate">
                                <span className="label label-large">Off Hire Date</span>
                                <input type='date' id="offHireDate" name="offHireDate" className='input modal-inputsize200' value={newItem.offHireDate} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className={errors["offHireDate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["offHireDate"]}</span>

                            <label className="input-group" htmlFor="pickupDate">
                                <span className="label label-large">Pick Up Date</span>
                                <input type='date' id="pickupDate" name="pickupDate" className='input modal-inputsize200' value={newItem.pickupDate} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className={errors["pickupDate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["pickupDate"]}</span>
                        </div>

                        <label className={newItem.itemTypeId === 38 || newItem.itemTypeId === 61 ? "input-group" : "hidden"} htmlFor="extraHours">
                            <span className="label label-large">{newItem.itemTypeId === 61 ? "Truck Hours" : "Hours"}</span>
                            <input type="number" min={0} step={1} className="input modal-inputsize200 textalignright" id="extraHours" name="extraHours" value={newItem.extraHours} onChange={(e) => handleCalcChange(e)} />
                        </label>
                        <span className={errors["extraHours"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["extraHours"]}</span>

                        <label className="input-group" htmlFor="quantity">
                            <span className="label label-large">Quantity {newItem.itemTypeId === 8 || newItem.itemTypeId === 50 || newItem.itemTypeId === 54 ? "(lm)" : ""}</span>
                            <input type="number" min="1" step="1" className="input modal-inputsize200 textalignright" id="quantity" name="quantity" value={newItem.quantity} onChange={(e) => handleCalcChange(e)} disabled={newItem.itemTypeId === 23 || (newItem.itemTypeId === 58 && newItem.item.includes("Septic"))} />
                        </label>
                        <span className={errors["quantity"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["quantity"]}</span>

                        <label className={newItem.itemTypeId === 50 ? "input-group" : "hidden"} htmlFor="fenceArea">
                            <span className="label label-large">Select Fence Area/s</span>
                            <div>
                                {newItem.fenceAreas.map((item, index) =>
                                    <span key={item.id} className="newJobFenceAreaContainer">
                                        <input className="checkbox" type="checkbox" id={"fenceArea" + index} name={"fenceArea" + index} checked={item.selected} onChange={(e) => updateFenceArea(e.target.checked, index)} disabled={isFullBoundry && item.id > 1}></input>
                                        <label className="label marginLeft20 label--padding5" htmlFor={"fenceArea" + index}>{item.name}</label>
                                    </span>
                                )}
                            </div>
                        </label>
                        <span className={errors["fenceArea"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fenceArea"]}</span>

                        <label className={newItem.itemTypeId === 50 ? "input-group" : "hidden"} htmlFor="showBracing">
                            <span className="label label-large">Include Bracing?</span>
                            <input className="checkbox" type="checkbox" id="showBracing" name="showBracing" checked={showBracing} onChange={(e) => setShowBracing(!showBracing)}></input>
                        </label>
                        
                        <label className={(newItem.itemTypeId === 50 && showBracing) || newItem.itemTypeId === 51 ? "input-group" : "hidden"} htmlFor="fenceBraceId">
                            <span className="label label-large">Bracing</span>
                            <select className="select modal-inputsize200" id="fenceBraceId" name="fenceBraceId" value={newItem.fenceBraceId} onChange={(e) => handleFenceBraceChange(e)} >
                                <option value={-1} defaultValue="-1"></option>
                                <option value={1}>Brace Every Panel</option>
                                <option value={2}>Brace Every 2nd Panel</option>
                                <option value={3}>Brace Every 3rd Panel</option>
                                <option value={4}>Brace Custom</option>
                            </select>
                        </label>
                        <span className={errors["fenceBraceId"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fenceBraceId"]}</span>

                        <div className={showBracing ? "" : "hidden"}>
                            <label className={newItem.itemTypeId === 50 ? "input-group" : "hidden"} htmlFor="fenceBraceQuantity">
                                <span className="label label-large">Brace Quantity (each)</span>
                                <input type="number" min="0" step="1" className="input modal-inputsize200 textalignright" id="fenceBraceQuantity" name="fenceBraceQuantity" value={newItem.fenceBraceQuantity} onChange={(e) => handleFenceBraceQtyChange(e)} />
                            </label>
                            <span className={errors["fenceBraceQuantity"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["fenceBraceQuantity"]}</span>

                            <span className={newItem.itemTypeId === 50 ? "label errors" : "hidden"}>Note: A separate brace item will be created when brace price &gt; 0</span>
                            <label className={newItem.itemTypeId === 50 ? "input-group" : "hidden"} htmlFor="fenceBracePrice">
                                <span className="label label-large">Brace Price</span>
                                <input className="input modal-inputsize200 textalignright" type="text" id="fenceBracePrice" name="fenceBracePrice" value={newItem.fenceBracePrice} disabled></input>
                            </label>
                        </div>

                        <label className={newItem.itemTypeId === 14 || newItem.itemTypeId === 17 ? "input-group" : "hidden"} htmlFor="itemId">
                            <span className="label label-large">Extra Type</span>
                            <div className="makeitflexspacebetween">
                                <select className="select jobschedule-newjob-extra--width" value={newItem.extras.itemId} disabled>
                                    <option hidden defaultValue="-1"></option>
                                    {extraItemOptions.map(item =>
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    )};
                                </select>
                                <input type="number" min="0" step="1" className="marginLeft10 jobschedule-qty--width" id="newitemquantity" name="newitemquantity" value={newItem.extras.quantity} onChange={(e) => handleQtyChange(e)} />
                            </div>

                            <label className="input-group " htmlFor="equipped">
                                <span className="label label-large">Equipped?</span>
                                <input type="checkbox" name="equipped" id="equipped" checked={newItem.extras.equipped} disabled></input>
                            </label>

                            <label className="input-group">
                                <span className="label label-large">Unit Includes</span>
                                <span>{
                                    (newItem.extras.quantity + extras.tables) + (newItem.extras.itemId === 52 ? " tables, " : " desks, ")
                                    + (((newItem.extras.itemId === 52 ? 4 : 2) * newItem.extras.quantity) + extras.chairs) + " chairs and air conditioning"}</span>
                            </label>
                        </label>
                        <label className={newItem.itemTypeId === 27 ? "input-group" : "hidden"} htmlFor="crossoversection">
                            <span className="label label-large">Extra Section (0.3m)</span>
                            <input type="number" min="0" step="1" className="marginLeft10 jobschedule-qty--width" id="newitemquantity" name="newitemquantity" value={newItem.extras.quantity} onChange={(e) => handleCrossoverQtyChange(e)} />
                        </label>

                        <div className={newItem.itemTypeId === 22 ? "hidden" : ""}>
                            <label className="input-group" htmlFor="initialHire">
                                <span className="label label-large">{newItem.itemTypeId === 61 || newItem.itemTypeId === 26 || newItem.itemTypeId === 36 || newItem.itemTypeId === 58 || newItem.itemTypeId === 59 || newItem.itemTypeId === 60 ? "Service Amount" : "Initial Hire"}</span>
                                <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="initialHire" name="initialHire" value={newItem.initialHire} onChange={(e) => handlePriceChange(e)}></input>
                            </label>
                            <span className={errors["initialHire"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["initialHire"]}</span>

                            <label className={newItem.isEws || newItem.itemTypeId === 23 || newItem.productGroupId === 6 || newItem.usePerMetrePricing ? "input-group" : "hidden"} htmlFor="cleaningFee">
                                <span className="label label-large">{cleaningConnectionHeading}</span>
                                <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="cleaningFee" name="cleaningFee" value={newItem.cleaningFee} onChange={(e) => handlePriceChange(e)}></input>
                            </label>
                            <label className={newItem.itemTypeId === 23 || newItem.itemTypeId === 61 ? "input-group" : "hidden"} htmlFor="trenchFee">
                                <span className="label label-large">{newItem.itemTypeId === 61 ? "Truck Hire" : "Trench Fee"}</span>
                                <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="trenchFee" name="trenchFee" value={newItem.trenchFee} onChange={(e) => handlePriceChange(e)}></input>
                            </label>
                            <span className={errors["trenchFee"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["trenchFee"]}</span>

                            <label className="input-group" htmlFor="travel">
                                <span className="label label-large">Travel</span>
                                <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="travel" name="travel" value={newItem.travel} onChange={(e) => handlePriceChange(e)}></input>
                            </label>
                        </div>

                        <div className={newItem.itemTypeId === 61 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="confinedSpaceAmount">
                                <span className="label label-large">Confined Space</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="confinedSpaceAmount" name="confinedSpaceAmount" value={newItem.confinedSpaceAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="sampleTestingAmount">
                                <span className="label label-large">Sample Testing</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="sampleTestingAmount" name="sampleTestingAmount" value={newItem.sampleTestingAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="wasteDisposalAmount">
                                <span className="label label-large">Waste Disposal</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="wasteDisposalAmount" name="wasteDisposalAmount" value={newItem.wasteDisposalAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                        </div>

                        <label className={newItem.isEvent ? "hidden" : "input-group"} htmlFor="fuelSurcharge">
                            <span className="label label-large">Fuel Surcharge</span>
                            <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="fuelSurcharge" name="fuelSurcharge" value={newItem.fuelSurcharge} onChange={(e) => handlePriceChange(e)}></input>
                        </label>

                        <label className="input-group" htmlFor="amount">
                            <span className="label label-large">Amount</span>
                            <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="amount" name="amount" value={newItem.amount} onChange={(e) => handleAmountChange(e)}></input>
                        </label>
                        <span className={errors["amount"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["amount"]}</span>

                        <label className={newItem.fenceBracePrice > 0 ? "input-group" : "hidden"} htmlFor="amount">
                            <span className="label label-large">Amount inc Bracing</span>
                            <input className="input modal-inputsize200 textalignright" type="text" min={0} step={0.01} id="amount" name="amount" value={newItem.amount + newItem.fenceBracePrice } disabled></input>
                        </label>

                        <label className={newItem.itemTypeId == 22 || newItem.installOnly || !newItem.extendable ? "hidden" : "input-group"} htmlFor="overhireRate">
                            <span className="label label-large">Additional Hire Rate</span>
                            <input className="input modal-inputsize200 textalignright" type="number" min={0} step={0.01} id="overhireRate" name="overhireRate" value={newItem.overhireRate} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["overhireRate"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["overhireRate"]}</span>

                        <label className="input-group" htmlFor="notes">
                            <span className="label label-large">Notes</span>
                            <textarea className="input job__noteSize" id="notes" name="notes" value={newItem.notes} onChange={(e) => handleChange(e)}></textarea>
                        </label>
                        <span className={errors["notes"] ? "label errors marginLeftLabelLarge" : "hidden"}>{errors["notes"]}</span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className={itemQuestions.showQuestions && itemQuestions.itemType === 1 ? "" : "hidden"}>
                        <button type="button" className="popup-button--yes" onClick={(e) => { setItemQuestions({showQuestions: false, itemType: 0}) }}>Back</button>
                    </div>
                    <div className={itemQuestions.showQuestions ? "hidden" : newItem.itemTypeId === 23 || newItem.productGroupId === 6 ? "makeitflexspacebetween mobile-width" : ""}>
                        <button type="button" className={newItem.itemTypeId === 23 || newItem.productGroupId === 6 ? "popup-button--yes" : "hidden"} onClick={(e) => { setItemQuestions({ showQuestions: true, itemType: newItem.productGroupId === 6 ? 2 : 1 }); clearError("epaQuestions"); setSaveDisabled(false); }}>Questions</button>
                        <button className="popup-button--yes" onClick={schedule} disabled={saveDisabled}>Schedule</button>
                    </div>
                    <div className={errors["epaQuestions"] ? "label errors" : "hidden"}>{errors["epaQuestions"]}</div>
                </Modal.Footer>
            </Modal>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobScheduleAddNewDataPopup;